<template>
  <div class="transition transition-overflow">
    <BasicText size="h5">Quels sont les onglets à dupliquer ?</BasicText>
    <div class="transition-items">
      <div
        v-for="item in listOnglets"
        :key="item.code"
      >
        <BasicCheckbox
          :label="item.intitule"
          :model-value="estRadionOn(item.code)"
          @update:model-value="updateOut(item.code)"
          class="checkbox"
          :disabled="!estOptionActive(item)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { OngletsAFLibellés } from '@/features/api/extranet/dto/Demande/AF'
import { OngletsCappLibellés } from '@/features/api/extranet/dto/Demande/CAPP'
import { OngletsCarLibellés } from '@/features/api/extranet/dto/Demande/CAR/OngletsCar.ts'
import { OngletsCPROLibellés } from '@/features/api/extranet/dto/Demande/CPRO'
import { OngletsCXPILibellés } from '@/features/api/extranet/dto/Demande/CXPI'
import { OngletsDRFLibellés } from '@/features/api/extranet/dto/Demande/CXPI/OngletsDRF'
import { OngletsOutilFpeFLibellés } from '@/features/api/extranet/dto/Demande/OUTILFPE/OngletsOutilFpe.ts'
import { OngletsPROALibellés } from '@/features/api/extranet/dto/Demande/PROA'
import { ONGLET_EMPLOYEUR, ONGLET_FORMATION } from '@/features/api/extranet/dto/Demande/shared'
import { TypesContextesAutorisesEnum } from '@/features/auth/context-app'
import type { CarteEvents, CarteProps } from '@/features/interface/machine-etats/types'
import { computed, onBeforeMount } from 'vue'
import type { SelectionnerOngletsIn, SelectionnerOngletsOut } from '../CreationDemandeConfiguration.types.ts'

  const props = defineProps<CarteProps<SelectionnerOngletsIn, SelectionnerOngletsOut>>()
  const emits = defineEmits<CarteEvents<SelectionnerOngletsOut>>()

  const onglets = computed(() => {
    switch (props.in.dispositif) {
      case 'CAPP':
        return OngletsCappLibellés
      case 'CPRO':
        return OngletsCPROLibellés
      case 'AF':
        return OngletsAFLibellés
      case 'PROA':
        return OngletsPROALibellés
      case 'FPE':
        if(props.in.typeDemande === 'SpecifiqueTT')
          return OngletsOutilFpeFLibellés
        if(props.in.typeDemande === 'ReservationFonds')
          return OngletsDRFLibellés
        if(props.in.sousDispositif === 'AfFpe')
          return OngletsAFLibellés
        if(props.in.sousDispositif === 'CAR')
          return OngletsCarLibellés

        return OngletsCXPILibellés
      default:
        throw new Error(`${props.in.dispositif} non supporté`)
    }
  })

  const estOptionActive = (item: { code: string }) => {
    return (
      (item.code !== ONGLET_EMPLOYEUR || estOngletEmployeurAutorisé.value) &&
      (item.code !== ONGLET_FORMATION || estOngletFormationAutorisés.value)
    )
  }

  const listOnglets = computed(() => Object.entries(onglets.value).map(([code, intitule]) => ({ code, intitule })))

  const estOngletEmployeurAutorisé = computed(() => {
    if (props.in.context === TypesContextesAutorisesEnum.Prestataire) return true
    if (props.in.dispositif === 'FPE' && props.in.typeDemande === 'Formation') return false
    return props.in.habilitations!.dispositifs.some((item) => item.code === props.in.dispositif)
  })

  const estOngletFormationAutorisés = computed(() => {
    if (props.in.context === TypesContextesAutorisesEnum.Adherent) return true
    return props.in.permissions === 'Ecriture'
  })

  onBeforeMount(() => {
    if (props.out) return

    // Sélectionne toutes les options par défaut
    const values = Object.keys(onglets.value)
    if (estOngletEmployeurAutorisé.value === false && props.in.dispositif !== 'FPE') {
      values.splice(values.indexOf(ONGLET_EMPLOYEUR), 1)
    }
    if (estOngletFormationAutorisés.value === false) {
      values.splice(values.indexOf(ONGLET_FORMATION), 1)
    }

    emits('outChanged', true, values as SelectionnerOngletsOut)
  })

  const estRadionOn = (onglet: string) => {
    return props.out?.includes(onglet as any)
  }

  const updateOut = (onglet: string) => {
    let newResult = [...(props.out ?? [])]
    if (newResult.includes(onglet as any)) {
      newResult = newResult.filter((item) => item !== onglet)
    } else {
      newResult.push(onglet as any)
    }
    emits('outChanged', newResult.length > 0, newResult as SelectionnerOngletsOut)
  }
</script>
<style lang="less" scoped>
  @import './ChoixCarte.less';
</style>

